import {useMemo, useState} from "react";

import {AsyncSearchCombobox} from "@/components/ui/async-search-combobox";
import {Item, ItemContent, ItemDescription, ItemTitle} from "@/components/ui/item";

import {useStoredMe} from "../../core/store/me";
import {ParamsOptions} from "../../core/types";
import {shiftsService} from "../../shifts/services";
import {useInfinitePropertiesWithDispatch} from "../hooks/use-infinite-properties-with-dispatch";
import {formatPropertyAddressLine} from "../lib/property-address";
import {Property} from "../types";

/** Stable default — `filter = {}` in params creates a new object every render and retriggers effects. */
const EMPTY_FILTER: ParamsOptions["filter"] = {};

type PropertiesComboboxSimpleProps = {
  value: Property | null;
  multiple?: false;
  onChange: (value: Property | null) => void;
};

type PropertiesComboboxMultipleProps = {
  value: Property[] | null;
  multiple: true;
  /** Callers may ignore null; component coerces empty selection to `[]`. */
  onChange: (value: Property[]) => void;
};

type PropertiesComboboxProps = {
  disabled?: boolean;
  className?: string;
  filter?: ParamsOptions["filter"];
  sort?: ParamsOptions["sort"];
  shouldFilterByUserProperties?: boolean;
  shouldRenderSelection?: boolean;
  placeholder?: string;
} & Omit<
  React.ComponentProps<typeof AsyncSearchCombobox<Property>>,
  | "items"
  | "onSearch"
  | "getItemKey"
  | "getItemLabel"
  | "isItemEqual"
  | "multiple"
  | "filter"
  | "value"
  | "onChange"
  | "placeholder"
> &
  (PropertiesComboboxSimpleProps | PropertiesComboboxMultipleProps);

export function PropertiesCombobox({
  value,
  multiple,
  filter = EMPTY_FILTER,
  sort = "name",
  onChange,
  shouldRenderSelection = true,
  shouldFilterByUserProperties = false,
  ...props
}: PropertiesComboboxProps) {
  const me = useStoredMe()!;
  const filterByUserProperties = shiftsService.getRoleMustFilterByUserProperties();
  const [nameSearch, setNameSearch] = useState("");

  const propertiesFilter = useMemo(() => {
    const next: Record<string, unknown> = {...filter};

    if (shouldFilterByUserProperties && filterByUserProperties) {
      next.ids = me.user_properties.map((p) => p.id).join(",");
    }

    const trimmed = nameSearch.trim();
    if (trimmed) {
      next.name = trimmed;
    }

    return next;
  }, [filterByUserProperties, me, filter, shouldFilterByUserProperties, nameSearch]);

  const queryOptions = useMemo(
    () => ({
      sort,
      filter: propertiesFilter,
    }),
    [sort, propertiesFilter],
  );

  const mergeDispatchSites = shouldFilterByUserProperties && filterByUserProperties;
  const query = useInfinitePropertiesWithDispatch(queryOptions, {mergeDispatchSites});
  const {properties, hasNextPage, fetchNextPage, isFetching, isFetchingNextPage} = query;
  const resolvedValue = useMemo(() => {
    if (multiple) {
      // RHF Controller can pass null on first mount when the field was unmounted
      // (e.g. shift form Patrol → Normal toggles Property field visibility).
      const selectedValues = Array.isArray(value) ? value : [];
      return selectedValues.map(
        (selected) => properties.find((property) => property.id === selected.id) ?? selected,
      );
    }

    const selected = value as Property | null;
    return selected
      ? (properties.find((property) => property.id === selected.id) ?? selected)
      : null;
  }, [multiple, properties, value]);

  const handleSearch = (term: string) => {
    setNameSearch(term);
  };

  // Wrap onChange to match AsyncSearchCombobox signature
  const handleChange = (newValue: Property | Property[] | null) => {
    if (multiple) {
      (onChange as PropertiesComboboxMultipleProps["onChange"])(
        (newValue as Property[] | null) ?? [],
      );
    } else {
      (onChange as PropertiesComboboxSimpleProps["onChange"])(newValue as Property | null);
    }
  };

  // Common props
  const commonProps = {
    items: properties,
    onSearch: handleSearch,
    onLoadMore: fetchNextPage,
    hasMore: hasNextPage,
    isLoading: isFetching,
    isLoadingMore: isFetchingNextPage,
    getItemKey: (item: Property) => item.id,
    getItemLabel: (item: Property) => item.name,
    isItemEqual: (item: Property, selected: Property | null) => item.id === selected?.id,
    placeholder: "Select property",
    loadingMessage: "Fetching users",
    loadingMoreMessage: "Fetching more users",
    renderItem: (option: Property) => (
      <Item size="sm" className="p-0">
        <ItemContent>
          <ItemTitle>{option.name}</ItemTitle>
          <ItemDescription className="text-xs">
            {formatPropertyAddressLine(option)}
          </ItemDescription>
        </ItemContent>
      </Item>
    ),
    ...props,
  };

  if (multiple) {
    return (
      <AsyncSearchCombobox<Property>
        {...commonProps}
        grouped={false as const}
        shouldRenderSelection={shouldRenderSelection}
        multiple={true}
        value={resolvedValue as Property[]}
        onChange={handleChange}
      />
    );
  } else {
    const single = resolvedValue as Property | null;
    // Remount when the list upgrades a stub label (e.g. "Property #153" → real name).
    // Combobox keeps the input string when isItemEqual stays true across that upgrade.
    const resolvedLabelKey = single ? `${single.id}:${single.name}` : "empty";

    return (
      <AsyncSearchCombobox<Property>
        key={resolvedLabelKey}
        {...commonProps}
        grouped={false as const}
        multiple={false}
        value={single}
        onChange={handleChange}
      />
    );
  }
}
